/**
 * Die Oberflaechenseite der Texterkennung: startet den Worker bei Bedarf und
 * macht aus Nachrichten Promises.
 *
 * Es gibt hoechstens einen Worker je Seite. Er wird erst gestartet, wenn
 * jemand die Texterkennung wirklich braucht -- die Modelle sind rund 16 MB,
 * die laedt niemand nebenbei (ADR 0033).
 */

import type { LeseOptionen, Leseergebnis } from "./erkenner.js";
import type { Auftrag, Nachricht } from "./protokoll.js";

export interface Ladestand {
  geladen: number;
  gesamt: number | null;
}

export interface Bereit {
  ms: number;
  bytes: number;
  threads: number;
}

export class Texterkennung {
  readonly #arbeiter: Worker;
  readonly #offen = new Map<
    number,
    { fertig: (e: Leseergebnis) => void; fehler: (f: Error) => void }
  >();
  readonly #beobachter = new Set<(stand: Ladestand) => void>();
  #nr = 0;
  #bereit: Promise<Bereit>;

  constructor() {
    this.#arbeiter = new Worker(new URL("./arbeiter.ts", import.meta.url), { type: "module" });
    let bereit!: (b: Bereit) => void;
    let gescheitert!: (f: Error) => void;
    this.#bereit = new Promise((j, n) => {
      bereit = j;
      gescheitert = n;
    });
    this.#arbeiter.onmessage = (e: MessageEvent<Nachricht>) => {
      const n = e.data;
      if (n.art === "ladestand") {
        this.#beobachter.forEach((b) => {
          b(n);
        });
      } else if (n.art === "bereit") bereit(n);
      else if (n.art === "ergebnis") {
        this.#offen.get(n.nr)?.fertig(n.ergebnis);
        this.#offen.delete(n.nr);
      } else if (n.nr === null) gescheitert(new Error(n.meldung));
      else {
        this.#offen.get(n.nr)?.fehler(new Error(n.meldung));
        this.#offen.delete(n.nr);
      }
    };
    this.#senden({ art: "laden" });
  }

  #senden(auftrag: Auftrag, uebergeben: Transferable[] = []): void {
    this.#arbeiter.postMessage(auftrag, uebergeben);
  }

  /** Wird erfuellt, sobald Modelle und Laufzeit geladen sind. */
  bereit(): Promise<Bereit> {
    return this.#bereit;
  }

  /** Meldet den Fortschritt beim Laden; liefert die Abmeldung. */
  beobachten(beobachter: (stand: Ladestand) => void): () => void {
    this.#beobachter.add(beobachter);
    return () => this.#beobachter.delete(beobachter);
  }

  /** Liest ein Bild. Die Pixel gehen dabei an den Worker ueber. */
  lesen(bild: ImageData, optionen: LeseOptionen): Promise<Leseergebnis> {
    const nr = ++this.#nr;
    return new Promise((fertig, fehler) => {
      this.#offen.set(nr, { fertig, fehler });
      const daten = bild.data.buffer;
      this.#senden(
        { art: "lesen", nr, bild: { breite: bild.width, hoehe: bild.height, daten }, optionen },
        [daten],
      );
    });
  }
}

let einzige: Texterkennung | null = null;

export function texterkennung(): Texterkennung {
  einzige ??= new Texterkennung();
  return einzige;
}
